import {openPrintWindow} from '../utils/printUtils';
import React,{useState,useEffect,useCallback} from 'react';
import {useLocation} from 'react-router-dom';
import {useAuth} from '../hooks/useAuth';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import {fmtDate,fmtDateTime} from '../utils/helpers';

const TABS=[['exams','👁️ Examinations'],['va','📊 Visual Acuity'],['refraction','🔭 Refraction'],['iop','💧 IOP'],['slitlamp','🔬 Slit Lamp'],['fundus','🫁 Fundus'],['procedures','⚕️ Procedures'],['orders','💊 Orders'],['rx','📋 Prescriptions']];
const EXAM_TYPES=['ROUTINE','FOLLOW_UP','EMERGENCY','PRE_OP','POST_OP','CONTACT_LENS','PEDIATRIC','GLAUCOMA','DIABETIC_RETINA'];
const PROC_STATUS_CSS:Record<string,string>={SCHEDULED:'bg-blue-100 text-[#02357A]',IN_PROGRESS:'bg-amber-100 text-amber-700',COMPLETED:'bg-green-100 text-green-700',CANCELLED:'bg-red-100 text-red-700',TEMPLATE:'bg-slate-100 text-slate-500'};
const IOP_METHODS=['NCT','GOLDMANN','TONOPEN','SCHIOTZ','ICARE'];
const FUND_METHODS=['DIRECT','INDIRECT','SLIT_LAMP_BIOMICROSCOPY','OCT','FUNDUS_PHOTO','FFA'];
const VA_OPTIONS=['6/6','6/9','6/12','6/18','6/24','6/36','6/60','3/60','2/60','1/60','CF','HM','PL','NPL'];
const SPH_OPTIONS=['+0.50','+0.75','+1.00','+1.25','+1.50','+1.75','+2.00','+2.25','+2.50','+2.75','+3.00','+3.50','+4.00','+4.50','+5.00','+5.50','+6.00','0.00','-0.50','-0.75','-1.00','-1.25','-1.50','-1.75','-2.00','-2.25','-2.50','-2.75','-3.00','-3.50','-4.00','-4.50','-5.00','-5.50','-6.00','-7.00','-8.00','-9.00','-10.00'];
const CYL_OPTIONS=['0.00','-0.25','-0.50','-0.75','-1.00','-1.25','-1.50','-1.75','-2.00','-2.50','-3.00','-3.50','-4.00'];
const NEAR_OPTIONS=['N5','N6','N8','N10','N12','N14','N18','N24','N36','N60'];
const COMMON_PROCS=['Phacoemulsification + IOL (Cataract Surgery)','SICS + IOL','YAG Laser Capsulotomy','Trabeculectomy (Glaucoma Surgery)','Ahmed Glaucoma Valve Implant','Retinal Detachment Surgery (Scleral Buckle)','Pars Plana Vitrectomy','Pan-Retinal Photocoagulation (PRP)','Focal Laser Photocoagulation','LASIK','PRK (Photorefractive Keratectomy)','Pterygium Excision','Chalazion Drainage','DCR (Dacryocystorhinostomy)','Strabismus Surgery','Corneal Transplant (PKP/DALK/DSAEK)','Intravitreal Injection (Anti-VEGF)','Botox for Blepharospasm'];
const ORDER_TYPES=['PHARMACY','LAB','RADIOLOGY','PROCEDURE'] as const;
const ORDER_TYPE_META:Record<string,{icon:string;label:string}>={PHARMACY:{icon:'💧',label:'Eye Medication'},LAB:{icon:'🔬',label:'Lab Investigation'},RADIOLOGY:{icon:'📷',label:'Imaging (OCT/B-scan/UBM)'},PROCEDURE:{icon:'⚕️',label:'Procedure'}};
const COMMON_EYE_ORDERS:Record<string,string[]>={
  PHARMACY:['Tropicamide 1% Eye Drops','Moxifloxacin 0.5% Eye Drops','Prednisolone Acetate 1% Eye Drops','Timolol Maleate 0.5% Eye Drops','Latanoprost 0.005% Eye Drops','Carboxymethylcellulose (Lubricant) Eye Drops','Atropine 1% Eye Drops','Cyclopentolate 1% Eye Drops','Ketorolac 0.5% Eye Drops','Anti-VEGF Intravitreal Injection (Bevacizumab/Ranibizumab)'],
  LAB:['Random Blood Sugar (Diabetic Retinopathy workup)','HbA1c','Lipid Profile','ESR/CRP (Uveitis workup)'],
  RADIOLOGY:['OCT (Optical Coherence Tomography)','B-Scan Ultrasonography','UBM (Ultrasound Biomicroscopy)','Fundus Fluorescein Angiography (FFA)','Corneal Topography','Specular Microscopy'],
  PROCEDURE:COMMON_PROCS,
};

export default function OphthalmologyPage(){
  const {user}=useAuth();
  const location=useLocation();
  const [tab,setTab]=useState<string>('exams');
  const [exams,setExams]=useState<any[]>([]);
  const [procs,setProcs]=useState<any[]>([]);
  const [stats,setStats]=useState<any>({});
  const [loading,setLoading]=useState(false);
  const [saving,setSaving]=useState(false);
  const [msg,setMsg]=useState('');
  const [search,setSearch]=useState('');
  const [selExam,setSelExam]=useState<any>(null);
  const [examFull,setExamFull]=useState<any>(null);
  const [incomingPatientName,setIncomingPatientName]=useState('');
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  // New Examination Panel
  const [showExam,setShowExam]=useState(false);

  // If navigated here from doctor dashboard / appointment with a specific patient, pre-fill and open the exam form
  useEffect(()=>{
    const st=(location.state as any);
    if(st?.patientId){
      setExamForm(f=>({...f,patientId:st.patientId}));
      setIncomingPatientName(st.patientName||'');
      setShowExam(true);
      const [first,...rest]=(st.patientName||'').split(' ');
      setOrderPatient({id:st.patientId,first_name:first||'',last_name:rest.join(' ')||'',uhid:''});
      loadOrders(st.patientId);
    }
  },[location.state]);
  const [examForm,setExamForm]=useState<any>({patientId:'',examinationType:'ROUTINE',
  chiefComplaint:'',historyOfPresentIllness:'',pastOcularHistory:'',
  systemicHistory:'',medications:'',plan:'',followUpDate:'',
  surgeonId:'',performedDate:'',intraOpNotes:'',
  yearOfRegistration:'',contactNumber:''});

  // Visual Acuity Panel
  const [showVA,setShowVA]=useState(false);
  const [vaForm,setVaForm]=useState({examinationId:'',patientId:'',odUnaided:'',odPinhole:'',odBestCorrected:'',odNear:'',osUnaided:'',osPinhole:'',osBestCorrected:'',osNear:'',notes:''});

  // Refraction Panel
  const [showRef,setShowRef]=useState(false);
  const [refForm,setRefForm]=useState({examinationId:'',patientId:'',refractionType:'SUBJECTIVE',odSph:'0.00',odCyl:'0.00',odAxis:'0',odAdd:'',odVision:'',osSph:'0.00',osCyl:'0.00',osAxis:'0',osAdd:'',osVision:'',prescriptionNotes:'',prescriptionType:'DISTANCE'});

  // IOP Panel
  const [showIop,setShowIop]=useState(false);
  const [iopForm,setIopForm]=useState({examinationId:'',patientId:'',method:'NCT',odIop:'',osIop:'',cctOd:'',cctOs:'',notes:''});

  // Slit Lamp Panel
  const [showSL,setShowSL]=useState(false);
  const [slForm,setSlForm]=useState({examinationId:'',patientId:'',odLids:'Normal',odConjunctiva:'Clear',odCornea:'Clear',odAc:'Deep and clear',odIris:'Normal',odPupil:'RAPD -ve',odLens:'Clear',osLids:'Normal',osConjunctiva:'Clear',osCornea:'Clear',osAc:'Deep and clear',osIris:'Normal',osPupil:'RAPD -ve',osLens:'Clear',notes:''});

  // Fundus Panel
  const [showFundus,setShowFundus]=useState(false);
  const [fundusForm,setFundusForm]=useState({examinationId:'',patientId:'',method:'DIRECT',odDisc:'Pink, margins well defined',odCdRatio:'0.3',odMacula:'Normal foveal reflex',odVessels:'Normal A:V ratio',odImpression:'',osDisc:'Pink, margins well defined',osCdRatio:'0.3',osMacula:'Normal foveal reflex',osVessels:'Normal A:V ratio',osImpression:'',notes:''});

  // Procedure Panel
  const [showProc,setShowProc]=useState(false);
  const [procForm,setProcForm]=useState({patientId:'',procedureName:'',procedureCode:'',eye:'OD',anesthesiaType:'TOPICAL',scheduledDate:'',iolModel:'',iolPower:'',preOpNotes:'',postOpInstructions:'',status:'SCHEDULED'});

  // Prescription Panel
  const [showRx,setShowRx]=useState(false);
  const [rxForm,setRxForm]=useState({patientId:'',examinationId:'',prescriptionType:'SPECTACLE',spectacleRx:{odSph:'',odCyl:'',odAxis:'',odAdd:'',osSph:'',osCyl:'',osAxis:'',osAdd:''},instructions:'',validUntil:''});

  // Orders Panel (CPOE)
  const [orderPatient,setOrderPatient]=useState<any>(null);
  const [ordersList,setOrdersList]=useState<any[]>([]);
  const [showOrderForm,setShowOrderForm]=useState(false);
  const [orderType,setOrderType]=useState<string>('PHARMACY');
  const defaultOrderForm={itemName:'',itemCode:'',dosage:'',route:'Topical (Eye Drop)',frequency:'QID (4x daily)',duration:'7 days',instructions:'',priority:'ROUTINE',eye:'OU'};
  const [orderForm,setOrderForm]=useState<any>({...defaultOrderForm});
  const resetOrderForm=()=>{setOrderForm({...defaultOrderForm});setOrderType('PHARMACY');};

  const loadOrders=async(patientId:string)=>{
    try{
      const r=await api.getCPOEOrders({patientId});
      setOrdersList(Array.isArray(r.data)?r.data:(r.data?.data||[]));
    }catch{setOrdersList([]);}
  };

  const saveOrder=async()=>{
    if(!orderForm.itemName){notify('⚠️ Select or enter an order item');return;}
    if(!orderPatient){notify('⚠️ No patient selected');return;}
    setSaving(true);
    try{
      await api.createCPOEOrder({
        ...orderForm,
        instructions:orderForm.eye&&orderType==='PHARMACY'?`${orderForm.eye} eye — ${orderForm.instructions||''}`.trim():orderForm.instructions,
        patientId:orderPatient.id||orderPatient.patient_id,doctorId:user?.id,orderType,contextSetting:'OP',
      });
      notify('✅ Order placed!');setShowOrderForm(false);resetOrderForm();
      loadOrders(orderPatient.id||orderPatient.patient_id);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const [e,p,s]=await Promise.all([
        api.getOphthalmExams?.({take:100}).catch(()=>({data:{data:[]}})),
        api.getOphthalmProcedures?.({take:100}).catch(()=>({data:{data:[]}})),
        api.getOphthalmStats?.().catch(()=>({data:{}})),
      ]);
      setExams(e?.data?.data||e?.data||[]);
      setProcs(p?.data?.data||p?.data||[]);
      setStats(s?.data||{});
    }catch{}
    setLoading(false);
  },[]);

  useEffect(()=>{load();},[load]);

  const openExamDetail=async(exam:any)=>{
    setSelExam(exam);
    setOrderPatient({id:exam.patient_id||exam.patientId,first_name:exam.first_name,last_name:exam.last_name,uhid:exam.uhid});
    loadOrders(exam.patient_id||exam.patientId);
    try{
      const r=await api.getOphthalmExamFull?.(exam.id).catch(()=>({data:{}}));
      setExamFull(r?.data||{});
    }catch{}
  };

  const saveExam=async()=>{
    if(!examForm.patientId||!examForm.chiefComplaint){notify('Patient ID and chief complaint required');return;}
    setSaving(true);
    try{
      const r=await api.createOphthalmExam?.(examForm);
      notify('✅ Examination created!');setShowExam(false);
      await load();
      // Auto-open the new exam
      if(r?.data?.id){openExamDetail(r.data);}
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const saveVA=async()=>{
    setSaving(true);
    try{
      await api.saveOphthalmVA?.(vaForm);
      notify('✅ Visual acuity saved!');setShowVA(false);
      if(selExam)openExamDetail(selExam);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const saveRef=async()=>{
    setSaving(true);
    try{
      await api.saveOphthalmRefraction?.(refForm);
      notify('✅ Refraction saved!');setShowRef(false);
      if(selExam)openExamDetail(selExam);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const saveIop=async()=>{
    setSaving(true);
    try{
      await api.saveOphthalmIop?.(iopForm);
      notify('✅ IOP recorded!');setShowIop(false);
      if(selExam)openExamDetail(selExam);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const saveSL=async()=>{
    setSaving(true);
    try{
      await api.saveOphthalmSlitLamp?.(slForm);
      notify('✅ Slit lamp findings saved!');setShowSL(false);
      if(selExam)openExamDetail(selExam);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const saveFundus=async()=>{
    setSaving(true);
    try{
      await api.saveOphthalmFundus?.(fundusForm);
      notify('✅ Fundus findings saved!');setShowFundus(false);
      if(selExam)openExamDetail(selExam);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const saveProc=async()=>{
    if(!procForm.patientId||!procForm.procedureName){notify('Patient and procedure required');return;}
    setSaving(true);
    try{
      await api.createOphthalmProcedure?.(procForm);
      notify('✅ Procedure scheduled!');setShowProc(false);await load();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const saveRx=async()=>{
    if(!rxForm.patientId){notify('Patient ID required');return;}
    setSaving(true);
    try{
      await api.createOphthalmPrescription?.(rxForm);
      notify('✅ Prescription saved!');setShowRx(false);
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}
    setSaving(false);
  };

  const openPanelForExam=(panel:string,exam:any):void=>{
    const base={examinationId:exam.id,patientId:exam.patient_id||exam.patientId};
    if(panel==='va'){setVaForm(f=>({...f,...base}));setShowVA(true);}
    else if(panel==='ref'){setRefForm(f=>({...f,...base}));setShowRef(true);}
    else if(panel==='iop'){setIopForm(f=>({...f,...base}));setShowIop(true);}
    else if(panel==='sl'){setSlForm(f=>({...f,...base}));setShowSL(true);}
    else if(panel==='fundus'){setFundusForm(f=>({...f,...base}));setShowFundus(true);}
    else if(panel==='rx'){setRxForm(f=>({...f,patientId:exam.patient_id||exam.patientId,examinationId:exam.id}));setShowRx(true);}
  };

  const printRefraction=(ref:any,exam:any)=>{
    const win=window.open('','_blank');if(!win)return;
    win.document.write(`<html><head><title>Refraction Prescription</title>
    <style>body{font-family:Arial;padding:20px;max-width:600px;margin:0 auto;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #ccc;padding:8px;text-align:center;} .header{text-align:center;margin-bottom:20px;} @media print{button{display:none;}}</style></head><body>
    <div class="header"><h2>👁️ OPHTHALMOLOGY PRESCRIPTION</h2><p>Patient: <b>${exam?.first_name||''} ${exam?.last_name||''}</b> | UHID: <b>${exam?.uhid||'—'}</b></p><p>Date: ${new Date().toLocaleDateString('en-IN')}</p></div>
    <table><thead><tr><th>Eye</th><th>Sph</th><th>Cyl</th><th>Axis</th><th>Add</th><th>Vision</th></tr></thead>
    <tbody>
    <tr><td><b>OD (R)</b></td><td>${ref?.od_sph||ref?.odSph||'0.00'}</td><td>${ref?.od_cyl||ref?.odCyl||'0.00'}</td><td>${ref?.od_axis||ref?.odAxis||'—'}</td><td>${ref?.od_add||ref?.odAdd||'—'}</td><td>${ref?.od_vision||ref?.odVision||'—'}</td></tr>
    <tr><td><b>OS (L)</b></td><td>${ref?.os_sph||ref?.osSph||'0.00'}</td><td>${ref?.os_cyl||ref?.osCyl||'0.00'}</td><td>${ref?.os_axis||ref?.osAxis||'—'}</td><td>${ref?.os_add||ref?.osAdd||'—'}</td><td>${ref?.os_vision||ref?.osVision||'—'}</td></tr>
    </tbody></table>
    ${ref?.prescription_type||ref?.prescriptionType?`<p><b>Type:</b> ${ref?.prescription_type||ref?.prescriptionType}</p>`:''}
    ${ref?.prescription_notes||ref?.prescriptionNotes?`<p><b>Notes:</b> ${ref?.prescription_notes||ref?.prescriptionNotes}</p>`:''}
    <p style="margin-top:30px;"><b>Signature:</b> ___________________________</p>
    <script>window.onload=()=>window.print();</script></body></html>`);
    win.document.close();
  };

  const filteredExams=exams.filter(e=>`${e.first_name||''} ${e.last_name||''} ${e.uhid||''}`.toLowerCase().includes(search.toLowerCase()));
  const filteredProcs=procs.filter(p=>p.status!=='TEMPLATE'&&`${p.first_name||''} ${p.last_name||''} ${p.procedure_name||''}`.toLowerCase().includes(search.toLowerCase()));

  return(
    <Layout title="Ophthalmology" actions={
      <div className="flex gap-2">
        <button onClick={()=>{setShowProc(true);setProcForm({patientId:'',procedureName:'',procedureCode:'',eye:'OD',anesthesiaType:'TOPICAL',scheduledDate:'',iolModel:'',iolPower:'',preOpNotes:'',postOpInstructions:'',status:'SCHEDULED'});}} className="btn-secondary text-sm">⚕️ Schedule Procedure</button>
        <button onClick={()=>{setShowExam(true);setExamForm({patientId:'',examinationType:'ROUTINE',chiefComplaint:'',historyOfPresentIllness:'',pastOcularHistory:'',systemicHistory:'',medications:'',plan:'',followUpDate:''});}} className="btn-primary text-sm">+ New Examination</button>
      </div>
    }>
      {msg&&<div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50">{msg}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[['Exams Today',stats.examsToday??'—','blue'],['Scheduled Procedures',stats.scheduledProcedures??'—','purple'],['Glaucoma Follow-ups',stats.glaucomaFollowUps??'—','amber'],['Cataract Scheduled',stats.cataractScheduled??'—','green']].map(([l,v,c])=>(
          <div key={String(l)} className="card p-3 text-center"><p className="text-xs text-slate-400">{l}</p><p className={`text-2xl font-bold text-${c}-600`}>{v}</p></div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(([k,l])=>(
          <button key={k} onClick={()=>{
            setTab(k);
            const panelMap:Record<string,string>={va:'va',refraction:'refraction',iop:'iop',slitlamp:'sl',fundus:'fundus',rx:'rx'};
            if(panelMap[k]){
              if(selExam){ openPanelForExam(panelMap[k],selExam); }
              else{ notify('⚠️ Select an examination from the list first (or create a new one)'); }
            }
          }} className={`flex-shrink-0 text-sm py-2 px-3 rounded-lg transition-all ${tab===k?'bg-white shadow font-semibold text-[#02357A]':'text-slate-600 hover:text-slate-800'}`}>{l}</button>
        ))}
      </div>
      {tab!=='procedures'&&tab!=='exams'&&tab!=='orders'&&!selExam&&(
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm text-amber-700">
          👈 Select an examination from the list (or create a new one) to record {TABS.find(([k])=>k===tab)?.[1].replace(/^\S+\s/,'')||'findings'}.
        </div>
      )}

      <input className="input w-full mb-4 text-sm" placeholder="Search patient name, UHID..." value={search} onChange={e=>setSearch(e.target.value)}/>

      {/* Examinations Tab */}
      {tab!=='procedures'&&tab!=='orders'&&(
        <div className="flex gap-4">
          {/* Exam list */}
          <div className="w-72 flex-shrink-0">
            <div className="card overflow-hidden">
              <div className="p-3 bg-slate-50 border-b flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-600 uppercase">Examinations</span>
                <button onClick={()=>setShowExam(true)} className="text-xs text-[#02357A] hover:underline">+ New</button>
              </div>
              {loading?<div className="p-4 text-center text-slate-400 text-sm">Loading...</div>
              :filteredExams.length===0?<div className="p-4 text-center text-slate-400 text-sm">No examinations</div>
              :<div className="divide-y max-h-[65vh] overflow-y-auto">
                {filteredExams.map((e:any)=>(
                  <div key={e.id} onClick={()=>openExamDetail(e)}
                    className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${selExam?.id===e.id?'bg-blue-50 border-l-4 border-[#015CA6]':''}`}>
                    <p className="font-medium text-sm">{e.first_name} {e.last_name}</p>
                    <p className="text-xs font-mono text-[#02357A]">{e.uhid}</p>
                    <p className="text-xs text-slate-400">{fmtDate(e.created_at)}</p>
                    <Badge label={e.examination_type||e.examinationType||'ROUTINE'} className="bg-blue-50 text-[#02357A] text-xs mt-1"/>
                  </div>
                ))}
              </div>}
            </div>
          </div>

          {/* Exam detail / entry workspace */}
          {!selExam?(
            <div className="flex-1 flex items-center justify-center text-slate-300">
              <div className="text-center"><p className="text-6xl mb-3">👁️</p><p>Select an examination to view / enter findings</p></div>
            </div>
          ):(
            <div className="flex-1 space-y-3 overflow-y-auto max-h-[70vh]">
              {/* Patient header */}
              <div className="card p-3 bg-gradient-to-r from-[#02357A] to-indigo-800 text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-lg">{selExam.first_name} {selExam.last_name}</p>
                    <p className="text-blue-200 text-sm font-mono">{selExam.uhid} · {selExam.examination_type||selExam.examinationType}</p>
                    <p className="text-blue-200 text-xs mt-1">{fmtDateTime(selExam.created_at)}</p>
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {[['VA','va'],['Ref','refraction'],['IOP','iop'],['SL','slitlamp'],['Fundus','fundus'],['Rx','rx']].map(([l,panel])=>(
                      <button key={panel} onClick={()=>{setTab(panel);openPanelForExam(panel,selExam);}} className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors">{l}</button>
                    ))}
                    <button onClick={()=>{setTab('orders');resetOrderForm();setShowOrderForm(true);}} className="bg-white/20 hover:bg-white/30 text-xs px-2 py-1 rounded transition-colors">💊 Order</button>
                  </div>
                </div>
                {selExam.chief_complaint&&<p className="text-blue-100 text-xs mt-2 italic">"{selExam.chief_complaint}"</p>}
              </div>

              {/* Visual Acuity display */}
              {examFull?.va&&(
                <div className="card p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-sm">📊 Visual Acuity</h3>
                    <button onClick={()=>openPanelForExam('va',selExam)} className="text-xs text-[#02357A] hover:underline">Edit</button>
                  </div>
                  <table className="w-full text-xs text-center">
                    <thead className="bg-slate-50"><tr><th className="p-1.5">Eye</th><th>Unaided</th><th>PH</th><th>BCVA</th><th>Near</th></tr></thead>
                    <tbody>
                      <tr className="border-t"><td className="p-1.5 font-semibold text-[#02357A]">OD (R)</td><td>{examFull.va.od_unaided||examFull.va.odUnaided||'—'}</td><td>{examFull.va.od_pinhole||examFull.va.odPinhole||'—'}</td><td className="font-bold">{examFull.va.od_best_corrected||examFull.va.odBestCorrected||'—'}</td><td>{examFull.va.od_near||examFull.va.odNear||'—'}</td></tr>
                      <tr className="border-t"><td className="p-1.5 font-semibold text-green-700">OS (L)</td><td>{examFull.va.os_unaided||examFull.va.osUnaided||'—'}</td><td>{examFull.va.os_pinhole||examFull.va.osPinhole||'—'}</td><td className="font-bold">{examFull.va.os_best_corrected||examFull.va.osBestCorrected||'—'}</td><td>{examFull.va.os_near||examFull.va.osNear||'—'}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}
              {!examFull?.va&&<div className="card p-3 text-center text-sm text-slate-400">No VA recorded · <button onClick={()=>openPanelForExam('va',selExam)} className="text-[#02357A] hover:underline">Record VA</button></div>}

              {/* IOP display */}
              {examFull?.iop&&(
                <div className="card p-3">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-semibold text-sm">💧 IOP ({examFull.iop.method})</h3><button onClick={()=>openPanelForExam('iop',selExam)} className="text-xs text-[#02357A] hover:underline">Edit</button></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 rounded-lg p-2 text-center"><p className="text-xs text-blue-500">OD (Right)</p><p className="text-2xl font-bold text-[#02357A]">{examFull.iop.od_iop||examFull.iop.odIop||'—'}</p><p className="text-xs text-blue-400">mmHg</p>{(examFull.iop.cct_od||examFull.iop.cctOd)&&<p className="text-xs text-slate-400 mt-1">CCT: {examFull.iop.cct_od||examFull.iop.cctOd}µm</p>}</div>
                    <div className="bg-green-50 rounded-lg p-2 text-center"><p className="text-xs text-green-500">OS (Left)</p><p className="text-2xl font-bold text-green-700">{examFull.iop.os_iop||examFull.iop.osIop||'—'}</p><p className="text-xs text-green-400">mmHg</p>{(examFull.iop.cct_os||examFull.iop.cctOs)&&<p className="text-xs text-slate-400 mt-1">CCT: {examFull.iop.cct_os||examFull.iop.cctOs}µm</p>}</div>
                  </div>
                  {(+examFull.iop.od_iop>21||+examFull.iop.os_iop>21)&&<p className="text-xs text-red-600 bg-red-50 rounded p-1.5 mt-2">⚠️ Elevated IOP detected — glaucoma workup recommended</p>}
                </div>
              )}
              {!examFull?.iop&&<div className="card p-3 text-center text-sm text-slate-400">No IOP recorded · <button onClick={()=>openPanelForExam('iop',selExam)} className="text-[#02357A] hover:underline">Record IOP</button></div>}

              {/* Refraction display */}
              {examFull?.refraction?.length>0&&(
                <div className="card p-3">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-semibold text-sm">🔭 Refraction ({examFull.refraction[0]?.refraction_type||'SUBJECTIVE'})</h3>
                    <div className="flex gap-2">
                      <button onClick={()=>printRefraction(examFull.refraction[0],selExam)} className="text-xs text-green-600 hover:underline">🖨️ Print Rx</button>
                      <button onClick={()=>openPanelForExam('refraction',selExam)} className="text-xs text-[#02357A] hover:underline">Add</button>
                    </div>
                  </div>
                  <table className="w-full text-xs text-center">
                    <thead className="bg-slate-50"><tr><th className="p-1.5">Eye</th><th>Sph</th><th>Cyl</th><th>Axis</th><th>Add</th><th>Vision</th></tr></thead>
                    <tbody>
                      {(['od','os'] as const).map(eye=>{
                        const r=examFull.refraction[0];
                        return <tr key={eye} className="border-t">
                          <td className="p-1.5 font-semibold">{eye.toUpperCase()} ({eye==='od'?'R':'L'})</td>
                          <td>{r[`${eye}_sph`]||r[`${eye}Sph`]||'0.00'}</td>
                          <td>{r[`${eye}_cyl`]||r[`${eye}Cyl`]||'0.00'}</td>
                          <td>{r[`${eye}_axis`]||r[`${eye}Axis`]||'—'}</td>
                          <td>{r[`${eye}_add`]||r[`${eye}Add`]||'—'}</td>
                          <td className="font-bold">{r[`${eye}_vision`]||r[`${eye}Vision`]||'—'}</td>
                        </tr>;
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Slit Lamp display */}
              {examFull?.slitLamp&&(
                <div className="card p-3">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-semibold text-sm">🔬 Slit Lamp</h3><button onClick={()=>openPanelForExam('sl',selExam)} className="text-xs text-[#02357A] hover:underline">Edit</button></div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {(['Lids','Conjunctiva','Cornea','AC','Iris','Pupil','Lens'] as const).map(field=>{
                      const sl=examFull.slitLamp;
                      const key=field.toLowerCase().replace(' ','');
                      return <React.Fragment key={field}>
                        <div className="bg-blue-50 rounded p-1.5"><p className="text-blue-400 text-xs">{field} OD</p><p>{sl[`od_${key}`]||sl[`od${field}`]||'—'}</p></div>
                        <div className="bg-green-50 rounded p-1.5"><p className="text-green-400 text-xs">{field} OS</p><p>{sl[`os_${key}`]||sl[`os${field}`]||'—'}</p></div>
                      </React.Fragment>;
                    })}
                  </div>
                </div>
              )}

              {/* Fundus display */}
              {examFull?.fundus&&(
                <div className="card p-3">
                  <div className="flex justify-between items-center mb-2"><h3 className="font-semibold text-sm">🫁 Fundus ({examFull.fundus.method})</h3><button onClick={()=>openPanelForExam('fundus',selExam)} className="text-xs text-[#02357A] hover:underline">Edit</button></div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {['Disc','C:D Ratio','Macula','Vessels'].map(f=>{
                      const fd=examFull.fundus;const k=f.toLowerCase().replace(':','').replace(' ','_');
                      return <React.Fragment key={f}>
                        <div className="bg-blue-50 rounded p-1.5"><p className="text-blue-400">{f} OD</p><p>{fd[`od_${k}`]||fd[`od${f.replace(' ','').replace(':','')}`]||'—'}</p></div>
                        <div className="bg-green-50 rounded p-1.5"><p className="text-green-400">{f} OS</p><p>{fd[`os_${k}`]||fd[`os${f.replace(' ','').replace(':','')}`]||'—'}</p></div>
                      </React.Fragment>;
                    })}
                  </div>
                  {(examFull.fundus.od_impression||examFull.fundus.odImpression)&&<p className="text-xs text-slate-600 mt-2 bg-slate-50 rounded p-1.5">{examFull.fundus.od_impression||examFull.fundus.odImpression}</p>}
                </div>
              )}

              {/* Plan / Notes */}
              {(selExam.plan||selExam.clinical_notes)&&(
                <div className="card p-3">
                  <h3 className="font-semibold text-sm mb-2">📋 Plan & Notes</h3>
                  {selExam.plan&&<p className="text-sm">{selExam.plan}</p>}
                  {selExam.clinical_notes&&<p className="text-xs text-slate-500 mt-1">{selExam.clinical_notes}</p>}
                  {selExam.follow_up_date&&<p className="text-xs text-[#02357A] mt-2">📅 Follow-up: {fmtDate(selExam.follow_up_date)}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Procedures Tab */}
      {tab==='procedures'&&(
        <div className="space-y-3">
          <div className="card overflow-hidden">
            <div className="p-3 bg-slate-50 border-b grid grid-cols-8 text-xs font-semibold text-slate-500 uppercase gap-2">
              <span className="col-span-2">Patient</span><span className="col-span-2">Procedure</span><span>Eye</span><span>Surgeon</span><span>Scheduled</span><span>Status</span>
            </div>
            {loading?<div className="p-8 text-center text-slate-400">Loading...</div>
            :filteredProcs.length===0?<div className="p-8 text-center text-slate-400">No procedures scheduled</div>
            :<div className="divide-y max-h-[60vh] overflow-y-auto">
              {filteredProcs.map((p:any)=>(
                <div key={p.id} className="grid grid-cols-8 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                  <div className="col-span-2"><p className="font-medium">{p.first_name} {p.last_name}</p><p className="text-xs font-mono text-[#02357A]">{p.uhid}</p></div>
                  <div className="col-span-2"><p className="font-medium text-xs">{p.procedure_name}</p>{p.iol_power&&<p className="text-xs text-slate-400">IOL: {p.iol_model} {p.iol_power}</p>}</div>
                  <span className="text-xs font-semibold">{p.eye||'—'}</span>
                  <span className="text-xs">{p.surgeon_first_name?`Dr.${p.surgeon_first_name[0]} ${p.surgeon_last_name}`:'—'}</span>
                  <span className="text-xs text-slate-500">{p.scheduled_date?fmtDate(p.scheduled_date):'—'}</span>
                  <Badge label={p.status} className={PROC_STATUS_CSS[p.status]||'bg-slate-100'}/>
                </div>
              ))}
            </div>}
          </div>
        </div>
      )}

      {/* Orders Tab — CPOE for Ophthalmology (Anti-VEGF injections, OCT, B-scan, eye medications, lab/path) */}
      {tab==='orders'&&(
        <div className="space-y-3">
          <div className="card p-3 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {orderPatient?`Ordering for: `:'Select an examination in the Examinations tab to order against that patient, or pick below:'}
              {orderPatient&&<span className="font-semibold text-slate-700"> {orderPatient.first_name||orderPatient.firstName} {orderPatient.last_name||orderPatient.lastName} ({orderPatient.uhid})</span>}
            </p>
            <button onClick={()=>{
              if(!orderPatient){notify('⚠️ Select an examination first so we know which patient to order for');return;}
              resetOrderForm();setShowOrderForm(true);
            }} className="btn-primary text-sm">+ New Order</button>
          </div>

          {ordersList.length===0?(
            <div className="card p-12 text-center"><span className="text-4xl block mb-2">💊</span><p className="text-slate-400">No orders placed for this patient yet</p></div>
          ):(
            <div className="card overflow-hidden">
              <div className="p-3 bg-slate-50 border-b grid grid-cols-6 text-xs font-semibold text-slate-500 uppercase gap-2">
                <span className="col-span-2">Item</span><span>Type</span><span>Priority</span><span>Status</span><span>Date</span>
              </div>
              <div className="divide-y max-h-[55vh] overflow-y-auto">
                {ordersList.map((o:any,i:number)=>(
                  <div key={i} className="grid grid-cols-6 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                    <div className="col-span-2"><p className="font-medium text-xs">{o.itemName||o.item_name}</p>{o.instructions&&<p className="text-xs text-amber-600 italic">{o.instructions}</p>}</div>
                    <span className="text-xs">{o.orderType||o.order_type}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full w-fit ${o.priority==='STAT'?'bg-red-100 text-red-700':o.priority==='URGENT'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-600'}`}>{o.priority}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full w-fit ${o.status==='COMPLETED'?'bg-green-100 text-green-700':o.status==='CANCELLED'?'bg-red-100 text-red-700':'bg-blue-100 text-[#02357A]'}`}>{o.status}</span>
                    <span className="text-xs text-slate-400">{fmtDate(o.createdAt||o.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


      <SlidePanel isOpen={showExam} onClose={()=>setShowExam(false)} title="New Eye Examination" icon="👁️" size="lg"
        actions={<button onClick={saveExam} disabled={saving} className="btn-primary w-full">{saving?'Creating...':'Create Examination'}</button>}>
        <div className="space-y-3">
          {incomingPatientName&&examForm.patientId&&(
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">
              👁️ Starting exam for <strong>{incomingPatientName}</strong> (from appointment)
            </div>
          )}
          <div><label className="label">Patient ID *</label><Input value={examForm.patientId} onChange={e=>setExamForm({...examForm,patientId:e.target.value})} placeholder="Enter patient UUID or use search"/></div>
          <div><label className="label">Examination Type</label>
            <Select value={examForm.examinationType} onChange={e=>setExamForm({...examForm,examinationType:e.target.value})}>
              {EXAM_TYPES.map(t=><option key={t}>{t}</option>)}
            </Select>
          </div>
          <div><label className="label">Chief Complaint *</label><Input value={examForm.chiefComplaint} onChange={e=>setExamForm({...examForm,chiefComplaint:e.target.value})} placeholder="e.g. Blurring of vision, Eye pain, Red eye, Floaters"/></div>
          <div><label className="label">History of Present Illness</label><textarea className="input w-full" rows={3} value={examForm.historyOfPresentIllness} onChange={e=>setExamForm({...examForm,historyOfPresentIllness:e.target.value})}/></div>
          <div><label className="label">Past Ocular History</label><Input value={examForm.pastOcularHistory} onChange={e=>setExamForm({...examForm,pastOcularHistory:e.target.value})} placeholder="Previous eye surgery, glasses, diseases..."/></div>
          <div><label className="label">Systemic History</label><Input value={examForm.systemicHistory} onChange={e=>setExamForm({...examForm,systemicHistory:e.target.value})} placeholder="Diabetes, Hypertension, Thyroid..."/></div>
          <div><label className="label">Current Medications</label><Input value={examForm.medications} onChange={e=>setExamForm({...examForm,medications:e.target.value})} placeholder="Eye drops, systemic medications..."/></div>
          <div><label className="label">Plan</label><textarea className="input w-full" rows={2} value={examForm.plan} onChange={e=>setExamForm({...examForm,plan:e.target.value})} placeholder="Treatment plan, investigations, surgery..."/></div>
          <div><label className="label">Follow-Up Date</label><Input type="date" value={examForm.followUpDate} onChange={e=>setExamForm({...examForm,followUpDate:e.target.value})}/></div>
        </div>
      </SlidePanel>

      {/* Visual Acuity Panel */}
      <SlidePanel isOpen={showVA} onClose={()=>setShowVA(false)} title="Visual Acuity" icon="📊"
        actions={<button onClick={saveVA} disabled={saving} className="btn-primary w-full">{saving?'Saving...':'Save VA'}</button>}>
        <div className="space-y-4">
          {(['OD (Right Eye)','OS (Left Eye)'] as const).map((label,idx)=>{
            const eye=idx===0?'od':'os';
            const fields={unaided:`${eye}Unaided`,pinhole:`${eye}Pinhole`,bestCorrected:`${eye}BestCorrected`,near:`${eye}Near`} as Record<string,keyof typeof vaForm>;
            return(
              <div key={label} className={`rounded-xl border-2 p-3 ${idx===0?'border-blue-200 bg-blue-50':'border-green-200 bg-green-50'}`}>
                <p className={`font-semibold text-sm mb-3 ${idx===0?'text-[#02357A]':'text-green-700'}`}>{label}</p>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="label text-xs">Unaided (Distance)</label>
                    <select className="input text-sm" value={(vaForm as any)[fields.unaided]||''} onChange={e=>setVaForm({...vaForm,[fields.unaided]:e.target.value})}>
                      <option value="">Select...</option>{VA_OPTIONS.map(v=><option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div><label className="label text-xs">Pinhole</label>
                    <select className="input text-sm" value={(vaForm as any)[fields.pinhole]||''} onChange={e=>setVaForm({...vaForm,[fields.pinhole]:e.target.value})}>
                      <option value="">Select...</option>{VA_OPTIONS.map(v=><option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div><label className="label text-xs">BCVA (Best Corrected)</label>
                    <select className="input text-sm" value={(vaForm as any)[fields.bestCorrected]||''} onChange={e=>setVaForm({...vaForm,[fields.bestCorrected]:e.target.value})}>
                      <option value="">Select...</option>{VA_OPTIONS.map(v=><option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div><label className="label text-xs">Near Vision</label>
                    <select className="input text-sm" value={(vaForm as any)[fields.near]||''} onChange={e=>setVaForm({...vaForm,[fields.near]:e.target.value})}>
                      <option value="">Select...</option>{NEAR_OPTIONS.map(v=><option key={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
          <div><label className="label">Notes</label><Input value={vaForm.notes} onChange={e=>setVaForm({...vaForm,notes:e.target.value})} placeholder="Special observations..."/></div>
        </div>
      </SlidePanel>

      {/* Refraction Panel */}
      <SlidePanel isOpen={showRef} onClose={()=>setShowRef(false)} title="Refraction" icon="🔭" size="lg"
        actions={<div className="flex gap-2"><button onClick={saveRef} disabled={saving} className="btn-primary flex-1">{saving?'Saving...':'Save Refraction'}</button><button onClick={()=>printRefraction(refForm,selExam)} className="btn-secondary px-3">🖨️ Print</button></div>}>
        <div className="space-y-4">
          <div><label className="label">Refraction Type</label>
            <Select value={refForm.refractionType} onChange={e=>setRefForm({...refForm,refractionType:e.target.value})}>
              {['SUBJECTIVE','OBJECTIVE (RETINOSCOPY)','AUTO REFRACTION','CYCLOPLEGIC'].map(t=><option key={t}>{t}</option>)}
            </Select>
          </div>
          {(['OD (Right Eye)','OS (Left Eye)'] as const).map((label,idx)=>{
            const eye=idx===0?'od':'os';
            return(
              <div key={label} className={`rounded-xl border-2 p-3 ${idx===0?'border-blue-200 bg-blue-50':'border-green-200 bg-green-50'}`}>
                <p className={`font-semibold text-sm mb-3 ${idx===0?'text-[#02357A]':'text-green-700'}`}>{label}</p>
                <div className="grid grid-cols-5 gap-2">
                  <div><label className="label text-xs">Sph</label>
                    <select className="input text-sm" value={(refForm as any)[`${eye}Sph`]} onChange={e=>setRefForm({...refForm,[`${eye}Sph`]:e.target.value})}>
                      {SPH_OPTIONS.map(v=><option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div><label className="label text-xs">Cyl</label>
                    <select className="input text-sm" value={(refForm as any)[`${eye}Cyl`]} onChange={e=>setRefForm({...refForm,[`${eye}Cyl`]:e.target.value})}>
                      {CYL_OPTIONS.map(v=><option key={v}>{v}</option>)}
                    </select>
                  </div>
                  <div><label className="label text-xs">Axis (°)</label>
                    <Input type="number" min="0" max="180" value={(refForm as any)[`${eye}Axis`]} onChange={e=>setRefForm({...refForm,[`${eye}Axis`]:e.target.value})} placeholder="0-180"/>
                  </div>
                  <div><label className="label text-xs">Add</label>
                    <Input value={(refForm as any)[`${eye}Add`]} onChange={e=>setRefForm({...refForm,[`${eye}Add`]:e.target.value})} placeholder="+1.00"/>
                  </div>
                  <div><label className="label text-xs">Vision</label>
                    <select className="input text-sm" value={(refForm as any)[`${eye}Vision`]} onChange={e=>setRefForm({...refForm,[`${eye}Vision`]:e.target.value})}>
                      <option value="">—</option>{VA_OPTIONS.map(v=><option key={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Prescription For</label>
              <Select value={refForm.prescriptionType} onChange={e=>setRefForm({...refForm,prescriptionType:e.target.value})}>
                {['DISTANCE','NEAR','BIFOCAL','PROGRESSIVE','READING'].map(t=><option key={t}>{t}</option>)}
              </Select>
            </div>
            <div><label className="label">Notes</label><Input value={refForm.prescriptionNotes} onChange={e=>setRefForm({...refForm,prescriptionNotes:e.target.value})}/></div>
          </div>
        </div>
      </SlidePanel>

      {/* IOP Panel */}
      <SlidePanel isOpen={showIop} onClose={()=>setShowIop(false)} title="Intraocular Pressure (IOP)" icon="💧"
        actions={<button onClick={saveIop} disabled={saving} className="btn-primary w-full">{saving?'Saving...':'Save IOP'}</button>}>
        <div className="space-y-4">
          <div><label className="label">Method</label>
            <Select value={iopForm.method} onChange={e=>setIopForm({...iopForm,method:e.target.value})}>
              {IOP_METHODS.map(m=><option key={m}>{m}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="font-semibold text-[#02357A] mb-2">OD — Right Eye</p>
              <Input type="number" step="0.1" value={iopForm.odIop} onChange={e=>setIopForm({...iopForm,odIop:e.target.value})} placeholder="e.g. 14.5" className="text-center text-xl font-bold"/>
              <p className="text-xs text-blue-400 mt-1">mmHg</p>
              <div className="mt-3"><label className="label text-xs">CCT (µm)</label><Input type="number" value={iopForm.cctOd} onChange={e=>setIopForm({...iopForm,cctOd:e.target.value})} placeholder="520"/></div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="font-semibold text-green-700 mb-2">OS — Left Eye</p>
              <Input type="number" step="0.1" value={iopForm.osIop} onChange={e=>setIopForm({...iopForm,osIop:e.target.value})} placeholder="e.g. 15.0" className="text-center text-xl font-bold"/>
              <p className="text-xs text-green-400 mt-1">mmHg</p>
              <div className="mt-3"><label className="label text-xs">CCT (µm)</label><Input type="number" value={iopForm.cctOs} onChange={e=>setIopForm({...iopForm,cctOs:e.target.value})} placeholder="520"/></div>
            </div>
          </div>
          {(+iopForm.odIop>21||+iopForm.osIop>21)&&<div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">⚠️ IOP &gt;21 mmHg detected. Consider glaucoma workup — VF, OCT RNFL, disc assessment.</div>}
          <div><label className="label">Notes</label><Input value={iopForm.notes} onChange={e=>setIopForm({...iopForm,notes:e.target.value})} placeholder="e.g. Dilated drops used, repeat check..."/></div>
        </div>
      </SlidePanel>

      {/* Slit Lamp Panel */}
      <SlidePanel isOpen={showSL} onClose={()=>setShowSL(false)} title="Slit Lamp Examination" icon="🔬" size="xl"
        actions={<button onClick={saveSL} disabled={saving} className="btn-primary w-full">{saving?'Saving...':'Save Slit Lamp'}</button>}>
        <div className="space-y-1">
          <p className="text-xs text-slate-500 mb-3">Normal defaults pre-filled. Edit any abnormal findings.</p>
          {[['Lids & Lashes','odLids','osLids'],['Conjunctiva','odConjunctiva','osConjunctiva'],['Cornea','odCornea','osCornea'],['Anterior Chamber','odAc','osAc'],['Iris','odIris','osIris'],['Pupil','odPupil','osPupil'],['Lens','odLens','osLens']].map(([label,odKey,osKey])=>(
            <div key={label} className="grid grid-cols-7 gap-2 items-center border-b py-2">
              <span className="col-span-1 text-xs font-semibold text-slate-600">{label}</span>
              <div className="col-span-3">
                <label className="text-xs text-blue-500">OD</label>
                <Input value={(slForm as any)[odKey]} onChange={e=>setSlForm({...slForm,[odKey]:e.target.value})} className="text-sm"/>
              </div>
              <div className="col-span-3">
                <label className="text-xs text-green-500">OS</label>
                <Input value={(slForm as any)[osKey]} onChange={e=>setSlForm({...slForm,[osKey]:e.target.value})} className="text-sm"/>
              </div>
            </div>
          ))}
          <div className="pt-2"><label className="label">Additional Notes</label><textarea className="input w-full" rows={2} value={slForm.notes} onChange={e=>setSlForm({...slForm,notes:e.target.value})}/></div>
        </div>
      </SlidePanel>

      {/* Fundus Panel */}
      <SlidePanel isOpen={showFundus} onClose={()=>setShowFundus(false)} title="Fundus Examination" icon="🫁" size="xl"
        actions={<button onClick={saveFundus} disabled={saving} className="btn-primary w-full">{saving?'Saving...':'Save Fundus'}</button>}>
        <div className="space-y-3">
          <div><label className="label">Method</label>
            <Select value={fundusForm.method} onChange={e=>setFundusForm({...fundusForm,method:e.target.value})}>
              {FUND_METHODS.map(m=><option key={m}>{m}</option>)}
            </Select>
          </div>
          {[['Disc','odDisc','osDisc'],['C:D Ratio','odCdRatio','osCdRatio'],['Macula','odMacula','osMacula'],['Vessels','odVessels','osVessels']].map(([label,odKey,osKey])=>(
            <div key={label} className="grid grid-cols-7 gap-2 items-center">
              <span className="col-span-1 text-xs font-semibold text-slate-600">{label}</span>
              <div className="col-span-3"><label className="text-xs text-blue-500">OD</label><Input value={(fundusForm as any)[odKey]} onChange={e=>setFundusForm({...fundusForm,[odKey]:e.target.value})}/></div>
              <div className="col-span-3"><label className="text-xs text-green-500">OS</label><Input value={(fundusForm as any)[osKey]} onChange={e=>setFundusForm({...fundusForm,[osKey]:e.target.value})}/></div>
            </div>
          ))}
          <div><label className="label">OD Impression</label><textarea className="input w-full" rows={2} value={fundusForm.odImpression} onChange={e=>setFundusForm({...fundusForm,odImpression:e.target.value})} placeholder="e.g. NPDR Grade II, Cup-disc suspect..."/></div>
          <div><label className="label">OS Impression</label><textarea className="input w-full" rows={2} value={fundusForm.osImpression} onChange={e=>setFundusForm({...fundusForm,osImpression:e.target.value})}/></div>
          <div><label className="label">Notes</label><Input value={fundusForm.notes} onChange={e=>setFundusForm({...fundusForm,notes:e.target.value})}/></div>
        </div>
      </SlidePanel>

      {/* Procedure Panel */}
      <SlidePanel isOpen={showProc} onClose={()=>setShowProc(false)} title="Schedule Ophthalmic Procedure" icon="⚕️" size="lg"
        actions={<button onClick={saveProc} disabled={saving} className="btn-primary w-full">{saving?'Scheduling...':'Schedule Procedure'}</button>}>
        <div className="space-y-3">
          <div><label className="label">Patient ID *</label><Input value={procForm.patientId} onChange={e=>setProcForm({...procForm,patientId:e.target.value})}/></div>
          <div><label className="label">Procedure *</label>
            <Select value={procForm.procedureName} onChange={e=>setProcForm({...procForm,procedureName:e.target.value})}>
              <option value="">Select procedure...</option>
              {COMMON_PROCS.map(p=><option key={p}>{p}</option>)}
              <option value="OTHER">Other (specify below)</option>
            </Select>
            {(procForm.procedureName==='OTHER'||!COMMON_PROCS.includes(procForm.procedureName))&&procForm.procedureName&&<Input className="mt-2" value={procForm.procedureName==='OTHER'?'':procForm.procedureName} onChange={e=>setProcForm({...procForm,procedureName:e.target.value})} placeholder="Custom procedure name"/>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Eye</label>
              <Select value={procForm.eye} onChange={e=>setProcForm({...procForm,eye:e.target.value})}>
                <option value="OD">OD (Right)</option><option value="OS">OS (Left)</option><option value="OU">OU (Both)</option>
              </Select>
            </div>
            <div><label className="label">Anesthesia</label>
              <Select value={procForm.anesthesiaType} onChange={e=>setProcForm({...procForm,anesthesiaType:e.target.value})}>
                {['TOPICAL','PERIBULBAR','RETROBULBAR','GENERAL','LOCAL'].map(a=><option key={a}>{a}</option>)}
              </Select>
            </div>
            <div><label className="label">Scheduled Date</label><Input type="datetime-local" value={procForm.scheduledDate} onChange={e=>setProcForm({...procForm,scheduledDate:e.target.value})}/></div>
            <div><label className="label">Status</label>
              <Select value={procForm.status} onChange={e=>setProcForm({...procForm,status:e.target.value})}>
                {['SCHEDULED','IN_PROGRESS','COMPLETED','CANCELLED'].map(s=><option key={s}>{s}</option>)}
              </Select>
            </div>
          </div>
          {procForm.procedureName.toLowerCase().includes('cataract')||procForm.procedureName.toLowerCase().includes('phaco')||procForm.procedureName.toLowerCase().includes('iol')?(
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
              <p className="text-xs font-bold text-[#02357A]">IOL Details</p>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label text-xs">IOL Model</label><Input value={procForm.iolModel} onChange={e=>setProcForm({...procForm,iolModel:e.target.value})} placeholder="e.g. AcrySof IQ, Tecnis"/></div>
                <div><label className="label text-xs">IOL Power</label><Input value={procForm.iolPower} onChange={e=>setProcForm({...procForm,iolPower:e.target.value})} placeholder="e.g. +21.50D"/></div>
              </div>
            </div>
          ):null}
          <div><label className="label">Pre-Op Notes</label><textarea className="input w-full" rows={2} value={procForm.preOpNotes} onChange={e=>setProcForm({...procForm,preOpNotes:e.target.value})}/></div>
          <div><label className="label">Post-Op Instructions</label><textarea className="input w-full" rows={2} value={procForm.postOpInstructions} onChange={e=>setProcForm({...procForm,postOpInstructions:e.target.value})} placeholder="Eye drops, precautions, follow-up..."/></div>
        </div>
      </SlidePanel>

      {/* CPOE Order Panel */}
      <SlidePanel isOpen={showOrderForm} onClose={()=>setShowOrderForm(false)} title="New Order" icon="💊" size="lg"
        actions={<button onClick={saveOrder} disabled={saving} className="btn-primary w-full">{saving?'Placing...':'📋 Place Order'}</button>}>
        <div className="space-y-4">
          {orderPatient&&(
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">
              Ordering for <strong>{orderPatient.first_name} {orderPatient.last_name}</strong> {orderPatient.uhid&&`(${orderPatient.uhid})`}
            </div>
          )}
          <div>
            <label className="label">Order Type *</label>
            <div className="grid grid-cols-4 gap-2">
              {ORDER_TYPES.map(t=>(
                <button key={t} type="button" onClick={()=>{setOrderType(t);setOrderForm({...defaultOrderForm});}}
                  className={`p-2 rounded-xl border-2 text-xs font-semibold flex flex-col items-center gap-1 transition-all
                    ${orderType===t?'border-[#015CA6] bg-[#02357A] text-white':'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  <span className="text-lg">{ORDER_TYPE_META[t].icon}</span>
                  <span>{t}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">{ORDER_TYPE_META[orderType].label} *</label>
            <select className="input" value={orderForm.itemName} onChange={e=>setOrderForm({...orderForm,itemName:e.target.value})}>
              <option value="">— Select —</option>
              {(COMMON_EYE_ORDERS[orderType]||[]).map(item=><option key={item} value={item}>{item}</option>)}
            </select>
            <input className="input mt-2" placeholder="Or type a custom item name..." value={orderForm.itemName}
              onChange={e=>setOrderForm({...orderForm,itemName:e.target.value})}/>
          </div>

          {orderType==='PHARMACY'&&(
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Eye</label>
                <select className="input" value={orderForm.eye} onChange={e=>setOrderForm({...orderForm,eye:e.target.value})}>
                  <option value="OD">OD (Right)</option><option value="OS">OS (Left)</option><option value="OU">OU (Both)</option>
                </select>
              </div>
              <div><label className="label">Frequency</label>
                <select className="input" value={orderForm.frequency} onChange={e=>setOrderForm({...orderForm,frequency:e.target.value})}>
                  {['OD (once daily)','BID (2x daily)','TID (3x daily)','QID (4x daily)','Q2H (every 2 hours)','HS (at bedtime)','SOS (as needed)'].map(f=><option key={f}>{f}</option>)}
                </select>
              </div>
              <div><label className="label">Duration</label><input className="input" value={orderForm.duration} onChange={e=>setOrderForm({...orderForm,duration:e.target.value})} placeholder="7 days"/></div>
              <div><label className="label">Priority</label>
                <select className="input" value={orderForm.priority} onChange={e=>setOrderForm({...orderForm,priority:e.target.value})}>
                  {['ROUTINE','URGENT','STAT'].map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
          )}
          {orderType!=='PHARMACY'&&(
            <div>
              <label className="label">Priority</label>
              <select className="input" value={orderForm.priority} onChange={e=>setOrderForm({...orderForm,priority:e.target.value})}>
                {['ROUTINE','URGENT','STAT'].map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
          )}

          <div><label className="label">Instructions / Notes</label><textarea className="input w-full" rows={2} value={orderForm.instructions} onChange={e=>setOrderForm({...orderForm,instructions:e.target.value})} placeholder="Special instructions..."/></div>
        </div>
      </SlidePanel>

      {/* Prescription Panel */}
      <SlidePanel isOpen={showRx} onClose={()=>setShowRx(false)} title="Ophthalmic Prescription" icon="📋"
        actions={<button onClick={saveRx} disabled={saving} className="btn-primary w-full">{saving?'Saving...':'Save Prescription'}</button>}>
        <div className="space-y-3">
          <div><label className="label">Patient ID *</label><Input value={rxForm.patientId} onChange={e=>setRxForm({...rxForm,patientId:e.target.value})}/></div>
          <div><label className="label">Type</label>
            <Select value={rxForm.prescriptionType} onChange={e=>setRxForm({...rxForm,prescriptionType:e.target.value})}>
              {['SPECTACLE','CONTACT_LENS','MEDICATION','DROPS','COMBINATION'].map(t=><option key={t}>{t}</option>)}
            </Select>
          </div>
          {(rxForm.prescriptionType==='SPECTACLE'||rxForm.prescriptionType==='CONTACT_LENS')&&(
            <div className="border rounded-xl p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase">Spectacle Rx</p>
              {(['OD','OS'] as const).map(eye=>{
                const k=eye.toLowerCase() as 'od'|'os';
                const rx=rxForm.spectacleRx;
                return(
                  <div key={eye} className={`rounded-lg p-2 ${eye==='OD'?'bg-blue-50':'bg-green-50'}`}>
                    <p className={`text-xs font-bold mb-1 ${eye==='OD'?'text-[#02357A]':'text-green-700'}`}>{eye}</p>
                    <div className="grid grid-cols-4 gap-1">
                      {['Sph','Cyl','Axis','Add'].map(f=>(
                        <div key={f}><label className="text-xs text-slate-400">{f}</label>
                          <Input value={rx[`${k}${f}`]||''} onChange={e=>setRxForm({...rxForm,spectacleRx:{...rx,[`${k}${f}`]:e.target.value}})} className="text-xs py-1" placeholder={f==='Axis'?'0-180':f==='Add'?'+2.00':'0.00'}/>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div><label className="label">Instructions</label><textarea className="input w-full" rows={3} value={rxForm.instructions} onChange={e=>setRxForm({...rxForm,instructions:e.target.value})} placeholder="Usage instructions, precautions..."/></div>
          <div><label className="label">Valid Until</label><Input type="date" value={rxForm.validUntil} onChange={e=>setRxForm({...rxForm,validUntil:e.target.value})}/></div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
